import { SEO } from "@/components/seo/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export default function PrivacyPolicy() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - SOL-VI",
    "description": "Privacy policy van SOL-VI voor bescherming van persoonlijke gegevens conform AVG",
    "publisher": {
      "@type": "Organization",
      "name": "SOL-VI",
      "url": "https://sol-vi.nl"
    }
  };

  return (
    <>
      <SEO 
        title="Privacy Policy - SOL-VI | Bescherming Persoonlijke Gegevens"
        description="Privacy policy van SOL-VI voor bescherming van persoonlijke gegevens conform AVG. Lees hoe we uw privacy respecteren."
        canonical="https://sol-vi.nl/privacy-policy"
        jsonLd={[structuredData]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
            
            <div className="prose prose-gray max-w-none">
              <p className="text-lg text-muted-foreground mb-8">
                Laatst bijgewerkt: 25 augustus 2024
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">1. Inleiding</h2>
                <p>
                  SOL-VI respecteert uw privacy en houdt zich aan de Algemene Verordening Gegevensbescherming (AVG). 
                  Deze privacy policy legt uit hoe wij uw persoonlijke gegevens verzamelen, gebruiken en beschermen.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">2. Contactgegevens</h2>
                <div className="bg-muted p-4 rounded-lg">
                  <p><strong>SOL-VI</strong></p>
                  <p>Prinswillemalexander straat, 6661 XH Elst</p>
                  <p>Telefoon: 06 17 10 57 86</p>
                  <p>E-mail: info@sol-vi.nl</p>
                  <p>KVK: 87825759</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">3. Welke gegevens verzamelen wij?</h2>
                <ul className="list-disc list-inside space-y-2">
                  <li>Contactgegevens (naam, adres, telefoon, e-mail)</li>
                  <li>Gegevens voor offertes en projecten</li>
                  <li>Technische website gegevens (cookies, IP-adres)</li>
                  <li>Communicatie- en correspondentiegegevens</li>
                  <li><strong>Affiliate tracking gegevens</strong> (via Daisycon en partners)</li>
                  <li>B2B bezoekersgegevens (via Leadinfo voor bedrijfsidentificatie)</li>
                </ul>
                
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                  <p className="text-sm">
                    <strong>Affiliate Marketing:</strong> Voor onze affiliate partnerships via Daisycon verzamelen we 
                    geanonimiseerde tracking gegevens om commissies correct toe te kennen. Deze gegevens bevatten geen 
                    persoonlijke informatie en worden conform de Daisycon verwerkersovereenkomst verwerkt.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">4. Waarom gebruiken wij uw gegevens?</h2>
                <ul className="list-disc list-inside space-y-2">
                  <li>Voor het uitvoeren van onze dienstverlening</li>
                  <li>Voor communicatie over projecten en offertes</li>
                  <li>Voor het verbeteren van onze website en diensten</li>
                  <li>Voor marketing (alleen met uw toestemming)</li>
                  <li><strong>Voor affiliate commissie tracking</strong> (via Daisycon partnership)</li>
                  <li>Voor B2B lead identificatie en analyse (via Leadinfo)</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-6 mb-3">Rechtsbasis voor gegevensverwerking:</h3>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li><strong>Contractuitvoering:</strong> Voor offertes en dienstverlening</li>
                  <li><strong>Toestemming:</strong> Voor marketing cookies en affiliate tracking</li>
                  <li><strong>Gerechtvaardigd belang:</strong> Voor website analyse en B2B lead identificatie</li>
                  <li><strong>Wettelijke verplichting:</strong> Voor administratie en boekhouding</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">5. Delen van gegevens</h2>
                <p className="mb-4">
                  Wij delen uw gegevens alleen met derden wanneer dit noodzakelijk is voor onze dienstverlening, 
                  zoals met installatiepatners of leveranciers. Wij verkopen nooit uw gegevens aan derden.
                </p>
                
                <h3 className="text-lg font-semibold mb-3">Gegevens delen met partners:</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Daisycon:</strong> Geanonimiseerde affiliate tracking data voor commissie-administratie</li>
                  <li><strong>Leadinfo:</strong> Website bezoekersdata voor B2B analyse (conform hun privacybeleid)</li>
                  <li><strong>Google Analytics:</strong> Geanonimiseerde gebruiksstatistieken</li>
                  <li><strong>Installatiepatners:</strong> Alleen projectgegevens noodzakelijk voor uitvoering</li>
                </ul>
                
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-4">
                  <p className="text-sm">
                    <strong>Daisycon Verwerkersovereenkomst:</strong> Voor onze affiliate partnership met Daisycon geldt 
                    hun standaard verwerkersovereenkomst die voldoet aan AVG-eisen. Deze kunt u vinden op:{' '}
                    <a href="https://www.daisycon.com/nl/standaard-verwerkersovereenkomst-publishers/" 
                       target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      daisycon.com/verwerkersovereenkomst
                    </a>
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">6. Bewaartermijn</h2>
                <p>
                  Wij bewaren uw gegevens niet langer dan noodzakelijk voor de doeleinden waarvoor ze zijn verzameld, 
                  conform wettelijke verplichtingen.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">7. Uw rechten</h2>
                <p>U heeft het recht om:</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Inzage te krijgen in uw gegevens</li>
                  <li>Uw gegevens te laten corrigeren</li>
                  <li>Uw gegevens te laten verwijderen</li>
                  <li>Bezwaar te maken tegen verwerking</li>
                  <li>Uw gegevens te laten overdragen</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">8. Beveiliging</h2>
                <p>
                  Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen 
                  ongeautoriseerde toegang, verlies of misbruik.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">9. Contact</h2>
                <p>
                  Voor vragen over deze privacy policy of uw gegevens kunt u contact opnemen via{' '}
                  <a href="mailto:info@sol-vi.nl" className="text-primary hover:underline">
                    info@sol-vi.nl
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
}